import React, { useContext, useMemo } from 'react';
import { Button, Space, Table, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';

import ApiSitesContext from '@totem/components/administration/apiSites/ApiSitesContext';
import {
  ApiSite,
  ApiSiteStatus,
} from '@totem/components/administration/apiSites/types';

const EMPTY_ID = '000000000000000000000000';

const statusColor: Record<ApiSiteStatus, string> = {
  Discovered: 'default',
  Active: 'success',
  Inactive: 'warning',
};

const displayOrgName = (site: ApiSite) => {
  const id = site.organization?.id;
  const name = site.organization?.name;
  if (!id || id === EMPTY_ID || !name) return 'Not Set';
  return name;
};

const ApiSitesTable = () => {
  const { filteredData, loading, onEdit, onStatusChange } =
    useContext(ApiSitesContext);

  const typeFilters = useMemo(
    () =>
      Array.from(new Set(filteredData.map((rec) => rec.type)))
        .sort()
        .map((typ) => ({ text: typ, value: typ })),
    [filteredData],
  );

  const statusFilters = useMemo(
    () =>
      Array.from(new Set(filteredData.map((rec) => rec.status)))
        .sort()
        .map((sts) => ({ text: sts, value: sts })),
    [filteredData],
  );

  const orgFilters = useMemo(
    () =>
      Array.from(
        new Map(
          filteredData.map((rec) => [
            rec.organization?.id ?? '',
            displayOrgName(rec),
          ]),
        ).entries(),
      )
        .sort((aEntry, bEntry) => aEntry[1].localeCompare(bEntry[1]))
        .map(([id, label]) => ({ text: label, value: id })),
    [filteredData],
  );

  const regionFilters = useMemo(
    () =>
      Array.from(
        new Map(
          filteredData
            .filter((rec) => rec.region?.id)
            .map((rec) => [rec.region.id, rec.region.name]),
        ).entries(),
      )
        .sort((aEntry, bEntry) => aEntry[1].localeCompare(bEntry[1]))
        .map(([id, name]) => ({ text: name, value: id })),
    [filteredData],
  );

  const buildingFilters = useMemo(
    () =>
      Array.from(
        new Map(
          filteredData
            .filter((rec) => rec.building?.id)
            .map((rec) => [rec.building.id, rec.building.name]),
        ).entries(),
      )
        .sort((aEntry, bEntry) => aEntry[1].localeCompare(bEntry[1]))
        .map(([id, name]) => ({ text: name, value: id })),
    [filteredData],
  );

  const columns: ColumnsType<ApiSite> = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      filters: typeFilters,
      onFilter: (value, record) => record.type === value,
      sorter: (aRow, bRow) => aRow.type.localeCompare(bRow.type),
    },
    {
      title: 'Display Name',
      dataIndex: 'displayName',
      key: 'displayName',
      sorter: (aRow, bRow) => aRow.displayName.localeCompare(bRow.displayName),
    },
    {
      title: 'Site Name',
      dataIndex: 'siteName',
      key: 'siteName',
      sorter: (aRow, bRow) => aRow.siteName.localeCompare(bRow.siteName),
    },
    {
      title: 'Site ID',
      dataIndex: 'siteId',
      key: 'siteId',
      sorter: (aRow, bRow) => aRow.siteId.localeCompare(bRow.siteId),
    },
    {
      title: 'Organization',
      key: 'organization',
      filters: orgFilters,
      onFilter: (value, record) => (record.organization?.id ?? '') === value,
      render: (_, record) => displayOrgName(record),
      sorter: (aRow, bRow) =>
        displayOrgName(aRow).localeCompare(displayOrgName(bRow)),
    },
    {
      title: 'Region',
      key: 'region',
      filters: regionFilters,
      onFilter: (value, record) => (record.region?.id ?? '') === value,
      render: (_, record) => record.region?.name ?? '',
      sorter: (aRow, bRow) =>
        (aRow.region?.name ?? '').localeCompare(bRow.region?.name ?? ''),
    },
    {
      title: 'Building',
      key: 'building',
      filters: buildingFilters,
      onFilter: (value, record) => (record.building?.id ?? '') === value,
      render: (_, record) => record.building?.name ?? '',
      sorter: (aRow, bRow) =>
        (aRow.building?.name ?? '').localeCompare(bRow.building?.name ?? ''),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      sorter: (aRow, bRow) => aRow.status.localeCompare(bRow.status),
      render: (status: ApiSiteStatus) => (
        <Tag color={statusColor[status] ?? 'default'}>{status}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const isInactive = record.status === 'Inactive';
        const statusLabel = isInactive ? 'Activate' : 'Deactivate';
        const nextStatus: ApiSiteStatus = isInactive ? 'Active' : 'Inactive';

        return (
          <Space>
            <Button size="small" onClick={() => onEdit(record)}>
              Edit
            </Button>
            <Tooltip title={statusLabel}>
              <Button
                size="small"
                onClick={() => onStatusChange(record.id, nextStatus)}
              >
                {statusLabel}
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Table<ApiSite>
      columns={columns}
      dataSource={filteredData}
      loading={loading}
      rowKey="id"
      pagination={{ defaultPageSize: 20, showSizeChanger: true }}
    />
  );
};

export default ApiSitesTable;
